interface UseStorageOptions {
  storage: 'local' | 'session';
}

/**
 * Type-safe sessionStorage/localStorage access
 * Better than accessing sessionStorage/localStorage directly because of type safety, mocks for testing etc
 * Also easier to track across the project if all storage access is done through this composable
 * The UseStorageOptions type is requesting storage type explicitly so that it's visible where data is saved just from the code,
 * more verbose but actually better for readability
 *
 * @example
 * // Session storage (default)
 * const { get, set, remove } = useStorage<string>('my-key', { storage: 'session' });
 * set('value');
 * const value = get(); // 'value'
 * remove();
 *
 * @example
 * // Local storage
 * const { get, set } = useStorage<{ name: string }>('user', { storage: 'local' });
 * set({ name: 'John' });
 */
export function useStorage<T>(key: string, options: UseStorageOptions) {
  const { storage } = options;

  const storageApi = storage === 'local' ? localStorage : sessionStorage;

  const get = (): T | null => {
    try {
      const item = storageApi.getItem(key);
      if (item === null || item === undefined) return null;

      return JSON.parse(item) as T;
    } catch {
      // Return raw string if JSON parsing fails (for plain strings)
      const item = storageApi.getItem(key);
      return item as T | null;
    }
  };

  const set = (value: T): void => {
    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      storageApi.setItem(key, serialized);
    } catch (error) {
      console.error(`[useStorage] Failed to set "${key}":`, error);
    }
  };

  const remove = (): void => {
    storageApi.removeItem(key);
  };

  const removeAll = (): void => {
    localStorage.clear();
  };

  return { get, set, remove, removeAll };
}
